import { PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { signOut } from '@/auth/auth';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';

import { getIdleReloadOptOut, setIdleReloadOptOut } from './sessionIdleReloadStorage';
import { APP_RELOAD_IDLE_MS, APP_RELOAD_WARNING_BEFORE_MS } from './sessionTimeoutConstants';

const reloadPage = () => {
  window.location.reload();
};

const formatRemainingTime = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Isolated subtree: only this component re-renders on the 1s countdown tick,
 * not the rest of the app under SessionTimeoutProvider.
 *
 * WCAG 2.2.1 (idle limit under 20h, not essential): no document meta refresh; warn before reload;
 * extend (Continue, activity, Escape/backdrop); remove timer for tab (opt-out); reload or sign out.
 */
function SessionTimeoutDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [idleReloadOptOut, setIdleReloadOptOutState] = useState(() => getIdleReloadOptOut());

  const timeoutEnabled = location.pathname !== '/signIn';
  const timerActive = timeoutEnabled && !idleReloadOptOut;

  const onExpire = useCallback(() => {
    reloadPage();
  }, []);

  const onReloadNow = useCallback(async () => {
    reloadPage();
  }, []);

  const { showWarning, remainingMs, reloadApplication, dismissWarning } = useSessionTimeout({
    timeoutMs: APP_RELOAD_IDLE_MS,
    warningBeforeMs: APP_RELOAD_WARNING_BEFORE_MS,
    onReloadNow,
    onExpire,
    enabled: timerActive,
  });

  const turnOffAutomaticReloadForSession = useCallback(() => {
    setIdleReloadOptOut(true);
    setIdleReloadOptOutState(true);
  }, []);

  const dialogOpen = showWarning && timerActive;

  return (
    <Dialog
      open={dialogOpen}
      onClose={dismissWarning}
      role="alertdialog"
      aria-labelledby="app-reload-title"
      aria-describedby="app-reload-description app-reload-activity-hint"
    >
      <DialogTitle id="app-reload-title">{t('appReloadTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ minWidth: 280 }}>
          <Typography id="app-reload-description" variant="body2" component="p">
            {t('appReloadDescription', { remaining: formatRemainingTime(remainingMs) })}
          </Typography>
          <Typography
            id="app-reload-activity-hint"
            variant="body2"
            component="p"
            sx={{ mt: 1.5 }}
          >
            {t('appReloadActivityExtendsHint')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={async () => {
            await signOut();
            navigate('/signIn');
          }}
        >
          {t('signOut')}
        </Button>
        <Button variant="text" color="inherit" onClick={turnOffAutomaticReloadForSession}>
          {t('appReloadTurnOffAutoReload')}
        </Button>
        <Button variant="outlined" onClick={() => void reloadApplication()}>
          {t('reloadAppNow')}
        </Button>
        <Button variant="contained" onClick={dismissWarning}>
          {t('appReloadContinueWorking')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SessionTimeoutProvider({ children }: PropsWithChildren) {
  return (
    <>
      {children}
      <SessionTimeoutDialog />
    </>
  );
}
